/**
 * Centralized token storage utility
 * Single source of truth for all token operations
 */

import { WEB_FCM_TOKEN_KEY } from "./constants";
import { clearDeviceRegistrationFailureCount } from "./deviceRegistrationLimiter";

export const TOKEN_KEYS = {
  USER_ACCESS_TOKEN: "user-access-token",
  USER_TOKEN: "user-token",
  REFRESH_TOKEN: "refresh-token",
  EXPIRES_IN: "expires-in",
  USER_ID: "user-id",
} as const;

export interface AuthToken {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number | null;
  userId: string;
}

export interface TokenStorage {
  accessToken: string | null;
  userAccessToken: string | null;
  refreshToken: string | null;
  expiresIn: string | null;
  userId: string | null;
}

export const tokenStorage = {
  setTokens: (tokens: AuthToken): void => {
    localStorage.setItem(TOKEN_KEYS.USER_TOKEN, tokens.id_token);
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    localStorage.setItem(TOKEN_KEYS.USER_ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(
      TOKEN_KEYS.EXPIRES_IN,
      tokens.expires_in?.toString() || ""
    );
    localStorage.setItem(TOKEN_KEYS.USER_ID, tokens.userId);
  },

  getTokens: (): TokenStorage => ({
    accessToken: localStorage.getItem(TOKEN_KEYS.USER_TOKEN),
    userAccessToken: localStorage.getItem(TOKEN_KEYS.USER_ACCESS_TOKEN),
    refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN),
    expiresIn: localStorage.getItem(TOKEN_KEYS.EXPIRES_IN),
    userId: localStorage.getItem(TOKEN_KEYS.USER_ID),
  }),

  getUserAccessToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.USER_ACCESS_TOKEN);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  },

  getUserId: (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.USER_ID);
  },

  /**
   * Check if token is expired or will expire soon
   * @param bufferMinutes - Minutes before expiration to consider token expired (default: 5)
   */
  isTokenExpired: (bufferMinutes: number = 5): boolean => {
    const expiresIn = localStorage.getItem(TOKEN_KEYS.EXPIRES_IN);
    if (!expiresIn) return true;

    const expirationTime = parseInt(expiresIn) * 1000;
    const now = Date.now();
    const bufferMs = bufferMinutes * 60 * 1000;
    const timeUntilExpiry = expirationTime - now;

    return timeUntilExpiry < bufferMs;
  },

  clearTokens: (): void => {
    localStorage.removeItem(TOKEN_KEYS.USER_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.USER_ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.EXPIRES_IN);
    localStorage.removeItem(TOKEN_KEYS.USER_ID);
    localStorage.removeItem(WEB_FCM_TOKEN_KEY);
    clearDeviceRegistrationFailureCount();
  },
};
